import { useEffect, useMemo, useState } from "react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { api, API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { canSee } from "@/lib/menus";
import { rupiah, tanggalID } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { useAuth } from "@/context/AuthContext";
import LabaProdukTable from "@/components/app/LabaProdukTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, Search, Wallet, Info, Pencil } from "lucide-react";

const Stat = ({ label, value, tone, testid, hint, action }) => (
  <div data-testid={testid} className="card-flat p-5">
    <div className="flex items-start justify-between gap-2">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      {action}
    </div>
    <div className={`mt-3 font-mono text-2xl tabular-nums ${tone}`} data-testid={testid ? `${testid}-value` : undefined}>{rupiah(value)}</div>
    {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
  </div>
);

const download = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k,v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

export default function LaporanSemua() {
  const [data, setData] = useState({ pemasukan: [], pengeluaran: [], total_pemasukan:0, total_pengeluaran:0, laba_bersih:0 });
  const [start, setStart] = useState(""); const [end, setEnd] = useState(""); const [q, setQ] = useState("");
  const { user } = useAuth();
  const isArang = user?.workspace_type === "arang";
  const [arang, setArang] = useState(null);
  useEffect(() => { if (isArang) api.get("/dashboard/arang").then((r) => setArang(r.data)).catch(() => {}); }, [isArang]);

  const nilaiStok = useMemo(() => (arang?.per_produk || []).reduce((t, p) => t + Math.max(0, Number(p.sisa_stok_kg || 0)) * Number(p.hpp_rata || 0), 0), [arang]);
  const [reloadKey, setReloadKey] = useState(0);
  const load = async () => {
    const r = await api.get("/laporan/semua", { params: { start, end, q } });
    setData(r.data);
    setReloadKey((k) => k + 1);
  };
  useEffect(()=>{ load(); }, []);
  const rows = useMemo(() => [
    ...data.pemasukan.map(x=>({ key:"i-"+x.id, tanggal:x.tanggal, tipe:"pemasukan", nama:x.nama_barang, kategori:x.kategori_nama, jumlah:x.total })),
    ...data.pengeluaran.map(x=>({ key:"o-"+x.id, tanggal:x.tanggal, tipe:"pengeluaran", nama:x.nama_vendor, kategori:x.kategori_nama, jumlah:x.nominal })),
  ].sort((a,b)=>String(b.tanggal||"").localeCompare(String(a.tanggal||""))), [data]);
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(rows, 10);

  return (
    <>
      <PageHeader title="Semua Laporan" subtitle="Ringkasan pemasukan, pengeluaran, dan laba bersih." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>download("/export/laporan-semua", {fmt:"pdf", start, end, q}, "laporan-semua.pdf")} className="rounded-sm" data-testid="dl-pdf">
            <Download size={14} className="mr-1"/> PDF
          </Button>
          <Button variant="outline" onClick={()=>download("/export/laporan-semua", {fmt:"xlsx", start, end, q}, "laporan-semua.xlsx")} className="rounded-sm" data-testid="dl-xlsx">
            <FileSpreadsheet size={14} className="mr-1"/> Excel
          </Button>
        </div>
      }/>
      <div className="p-4 md:p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Stat testid="rpt-pemasukan" label="Total Pemasukan" value={data.total_pemasukan} tone="text-emerald-700"/>
          <Stat testid="rpt-pengeluaran" label="Total Pengeluaran" value={data.total_pengeluaran} tone="text-rose-700"/>
          <Stat testid="rpt-laba" label="Laba Bersih" value={data.laba_bersih} tone="text-slate-900"/>
        </div>
        <div className="space-y-3" data-testid="rpt-kas-section">
          <div className="flex items-center gap-2"><Wallet size={16} className="brand-text"/><h3 className="font-semibold text-slate-900 text-sm">Arus Kas Riil (Free Cash Flow)</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            <Stat testid="rpt-kas-masuk" label="Kas Masuk Riil" value={data.kas_masuk || 0} tone="text-emerald-700" hint={`Uang benar-benar diterima. Belum dibayar: ${rupiah(data.piutang_periode || 0)}`}/>
            <Stat testid="rpt-kas-keluar" label="Kas Keluar" value={data.kas_keluar || 0} tone="text-rose-700" hint="Semua pengeluaran yang sudah dibayar"/>
            <Stat testid="rpt-fcf" label="Arus Kas Bersih Periode" value={data.arus_kas_bersih || 0} tone={(data.arus_kas_bersih || 0) < 0 ? "text-rose-700" : "text-slate-900"} hint="Kas masuk riil − kas keluar"/>
            <Stat testid="rpt-saldo-kas" label="Total Cash (semua akun)" value={data.saldo_kas_riil || 0} tone={(data.saldo_kas_riil || 0) < 0 ? "text-rose-700" : "text-blue-700"}
              hint={`Saldo riil s/d ${end || "hari ini"} — akun milik pabrik ini + porsi di akun Pusat (Gabungan = semua akun)`}
              action={canSee(user, "akun") && <Link to="/kas-bank/akun" title="Kelola akun Kas & Bank" className="p-1 rounded-sm hover:bg-slate-100" data-testid="rpt-kelola-akun"><Pencil size={13} className="text-slate-500"/></Link>}/>
          </div>
          <div className="card-flat overflow-hidden" data-testid="rpt-akun-saldo">
            <div className="px-4 py-2.5 border-b bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">Saldo per Akun Kas & Bank</div>
            <div className="divide-y">
              {(data.akun_saldo || []).map((a) => (
                <div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`rpt-akun-${a.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800">{a.nama}</span>
                    {a.no_rekening && <span className="text-slate-400 text-xs ml-2">{a.no_rekening}</span>}
                    {a.is_default && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700">Default</span>}
                    {isArang && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-sm ${a.pabrik_id ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700"}`}>{a.pabrik_nama || "Pusat"}</span>}
                    {a.porsi_saldo != null && <span className="block text-[11px] text-violet-700">Porsi pabrik ini: {rupiah(a.porsi_saldo)} (saldo akun Pusat total {rupiah(a.saldo)})</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`font-mono ${(a.porsi_saldo ?? a.saldo) < 0 ? "text-rose-700" : "text-slate-900"}`}>{rupiah(a.porsi_saldo ?? a.saldo)}</span>
                    {canSee(user, "akun") && <Link to={`/kas-bank/akun/${a.id}/mutasi`} className="text-xs brand-text hover:underline">Mutasi</Link>}
                  </div>
                </div>
              ))}
              {(!data.akun_saldo || data.akun_saldo.length === 0) && <div className="px-4 py-3 text-sm text-slate-400">Belum ada akun.</div>}
            </div>
          </div>
          <div className="flex items-start gap-2 text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="rpt-kas-info">
            <Info size={14} className="mt-0.5 shrink-0"/>
            <span><b>Total Pemasukan</b> = omzet penjualan (tidak berkurang saat ada pengeluaran). <b>Laba Bersih</b> = pemasukan − pengeluaran pada periode ini; bisa minus bila biaya (kayu, operasional, pembelian) sudah keluar tetapi arangnya belum terjual{isArang ? ` — nilai stok arang yang belum terjual saat ini ${rupiah(nilaiStok)}` : ""}. <b>Total Cash</b> = jumlah saldo riil semua akun Kas & Bank (tunai, rekening, e-wallet). Isi saldo awal tiap akun di menu Kas & Bank → Akun agar cocok dengan uang di tangan & rekening koran.</span>
          </div>
        </div>
        {isArang && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="rpt-arang-cards">
            <Stat testid="rpt-nilai-stok" label="Nilai Stok Arang (belum terjual)" value={nilaiStok} tone="text-blue-700" hint="Sisa stok × HPP rata-rata per produk"/>
            <Stat testid="rpt-piutang" label="Piutang Berjalan" value={arang?.piutang || 0} tone="text-amber-700"/>
            <Stat testid="rpt-biaya-produksi" label="Total Biaya Produksi (semua produk)" value={arang?.total_biaya_produksi || 0} tone="text-rose-700"/>
          </div>
        )}
        <div className="card-flat p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><Label>Dari Tanggal</Label><Input type="date" value={start} onChange={(e)=>setStart(e.target.value)}/></div>
          <div><Label>Sampai Tanggal</Label><Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)}/></div>
          <div className="md:col-span-2"><Label>Pencarian</Label><Input placeholder="Cari nama, kategori..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="rpt-search"/></div>
          <div className="md:col-span-4"><Button onClick={load} className="brand-btn rounded-sm" data-testid="rpt-filter"><Search size={14} className="mr-1"/> Terapkan Filter</Button></div>
        </div>
        {isArang && reloadKey > 0 && <LabaProdukTable start={start} end={end} reloadKey={reloadKey}/>}
        <div className="card-flat overflow-hidden" data-testid="rpt-table-card">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]" data-testid="rpt-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[12px] uppercase tracking-wide text-slate-500">
              <th className="p-3 w-12 text-center">No</th><th className="p-3">Tanggal</th><th className="p-3">Tipe</th><th className="p-3">Nama</th><th className="p-3">Kategori</th><th className="p-3 text-right">Jumlah</th>
            </tr></thead>
            <tbody>
              {rows.length===0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Tidak ada data.</td></tr>}
              {paged.map((r, i)=>(
                <tr key={r.key} className="border-b border-slate-100" data-testid={`rpt-row-${r.key}`}>
                  <td className="p-3 text-center text-slate-500 font-mono text-xs" data-testid={`rpt-no-${r.key}`}>{(page - 1) * pageSize + i + 1}</td>
                  <td className="p-3 whitespace-nowrap">{tanggalID(r.tanggal)}</td>
                  <td className="p-3">{r.tipe==="pemasukan"
                    ? <span className="text-[11px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">Pemasukan</span>
                    : <span className="text-[11px] px-2 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Pengeluaran</span>}</td>
                  <td className="p-3">{r.nama}</td><td className="p-3">{r.kategori||"-"}</td>
                  <td className={`p-3 text-right font-mono font-semibold ${r.tipe==="pemasukan"?"text-emerald-700":"text-rose-700"}`}>{r.tipe==="pemasukan"?"+":"-"}{rupiah(r.jumlah)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="rpt-pagination"/>
        </div>
      </div>
    
    </>
  );
}
